import globalFeatures from '../constants/features';
import { isFeatureEnabled } from '../features';

const get = require('lodash/get');
const find = require('lodash/find');
const head = require('lodash/head');
const tail = require('lodash/tail');
const optionsSuccess = require('@flowio/request/lib/success');

const ShopifyCart = require('./ShopifyCart');
const FlowCart = require('./FlowCart');
const FlowCartItem = require('./FlowCartItem');

// TODO: Consider only calling `flowCart` when an experience is defined. The API will throw errors
// when it has trouble localizing things
class Cart {
  constructor({ debug = false, flowCartId, flowApiBaseURI } = {}) {
    this.debug = debug;
    this.flowCartId = flowCartId;
    this.flowApiBaseURI = flowApiBaseURI;

    this.shopifyCart = new ShopifyCart({
      debug: this.debug,
      flowApiBaseURI,
    });
    this.flowCart = new FlowCart({
      debug: this.debug,
      cartId: this.flowCartId,
      apiBaseURI: this.flowApiBaseURI,
    });
  }

  static getFlowCartItem(flowCart, id, properties) {
    const items = get(flowCart, 'items', []);
    let item = find(items, { variant_id: id, properties });

    if (!item) {
      item = find(items, { variant_id: id });
    }


    if (item) {
      return new FlowCartItem(item);
    }

    return {};
  }

  /**
   * Items need to be added to shopify individually. Recurses through `options.items` until they're
   * all added with localized properties from Flow Cart.
   *
   * Intended to be used with Cart.addItems
   */
  addShopifyLocalizedItems(flowCart, options) {
    const items = get(options, 'items', []);
    const currentItem = head(items);
    const remainingItems = tail(items);

    if (currentItem) {
      const flowCartItem = Cart.getFlowCartItem(flowCart, currentItem.id, currentItem.properties);
      this.shopifyCart.addItem({
        ...options,
        id: currentItem.id,
        quantity: currentItem.quantity,
        properties: {
          ...currentItem.properties,
          ...flowCartItem.getLocalizedProperties(),
        },
        onSuccess: (shopifyCart) => {
          if (remainingItems.length) {
            this.addShopifyLocalizedItems(flowCart, { ...options, items: remainingItems });
          } else {
            optionsSuccess(options, { flowCart, shopifyCart });
          }
        },
      });
    }
  }

  // TODO: Sync subtotal on cart attributes.. use Update to add items instead?
  addItem(options = {}) {
    this.flowCart.addItem({
      ...options,
      onSuccess: (flowCart) => {
        const flowCartItem = Cart.getFlowCartItem(flowCart, options.id, options.properties);
        this.shopifyCart.addItem({
          ...options,
          properties: {
            ...options.properties,
            ...flowCartItem.getLocalizedProperties(),
          },
          onSuccess: (shopifyCart) => {
            optionsSuccess(options, { flowCart, shopifyCart });
          },
        });
      },
    });
  }

  addItems(options = {}) {
    this.flowCart.addItems({
      ...options,
      onSuccess: (flowCart) => {
        this.addShopifyLocalizedItems(flowCart, options);
      },
    });
  }

  updateItem(options = {}) {
    this.flowCart.updateItem({
      ...options,
      onSuccess: (flowCart) => {
        const flowCartItem = Cart.getFlowCartItem(flowCart, options.id, options.properties);
        this.shopifyCart.updateItem({
          ...options,
          properties: {
            ...options.properties,
            ...flowCartItem.getLocalizedProperties(),
          },
          onSuccess: (shopifyCart) => {
            optionsSuccess(options, { flowCart, shopifyCart });
          },
        });
      },
    });
  }

  getShopifyCart(options, flowCart = {}) {
    return this.shopifyCart.getCart({
      ...options,
      onSuccess: (shopifyCart) => {
        optionsSuccess(options, { flowCart, shopifyCart });
      },
    });
  }

  getShopifyAndFlowCart(options) {
    return this.flowCart.getCart({
      ...options,
      onSuccess: (flowCart) => {
        this.shopifyCart.getCart({
          ...options,
          onSuccess: (shopifyCart) => {
            optionsSuccess(options, { flowCart, shopifyCart });
          },
        });
      },
    });
  }

  getCart(options = {}) {
    return isFeatureEnabled(globalFeatures.ENABLE_CARTV2)
      .then((isEnableCartV2Enabled) => {
        if (isEnableCartV2Enabled) {
          return this.getShopifyCart(options);
        }
        // backwards compatible for clients without ENABLE_CARTV2 flag
        return this.getShopifyAndFlowCart(options);
      });
  }

  removeItem(options = {}) {
    this.flowCart.removeItem({
      ...options,
      onSuccess: (flowCart) => {
        this.shopifyCart.removeItem({
          ...options,
          onSuccess: (shopifyCart) => {
            optionsSuccess(options, { flowCart, shopifyCart });
          },
        });
      },
    });
  }

  setShopifyCartAttributes(options = {}) {
    return this.shopifyCart.setAttributes({
      ...options,
      onSuccess: (shopifyCart) => {
        optionsSuccess(options, { shopifyCart });
      },
    });
  }

  syncCartAttributes(options = {}) {
    return this.flowCart.setAttributes({
      ...options,
      onSuccess: (flowCart) => {
        this.shopifyCart.setAttributes({
          ...options,
          onSuccess: (shopifyCart) => {
            optionsSuccess(options, { flowCart, shopifyCart });
          },
        });
      },
    });
  }

  setAttributes(options = {}) {
    return isFeatureEnabled(globalFeatures.ENABLE_CARTV2)
      .then((isEnableCartV2Enabled) => {
        if (isEnableCartV2Enabled) {
          return this.setShopifyCartAttributes(options);
        }
        // backwards compatible for clients without ENABLE_CARTV2 flag
        return this.syncCartAttributes(options);
      });
  }

  clear(options = {}) {
    this.flowCart.clear({
      ...options,
      onSuccess: (flowCart) => {
        this.shopifyCart.clear({
          ...options,
          onSuccess: (shopifyCart) => {
            optionsSuccess(options, { flowCart, shopifyCart });
          },
        });
      },
    });
  }
}

module.exports = Cart;



// WEBPACK FOOTER //
// ./static/js/v0/cart_unified/Cart.js